import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Download,
  ExternalLink,
  FileUp,
  Loader2,
  Package,
  PackageSearch,
  Plus,
  Search,
  Truck,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ALERT_SCORTA_LABELS, farmGet } from "./shared";

const TABS = [
  { id: "catalogo", label: "Catalogo prodotti" },
  { id: "scorte", label: "Scorte" },
  { id: "movimenti", label: "Rilevazioni" },
  { id: "alert", label: "Alert" },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 16);
  }
}

function formatEuro(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `€ ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function productStatus(p, scortaMatch) {
  const alerts = scortaMatch?.alerts || [];
  if (alerts.includes("da_verificare")) {
    return { label: "Da identificare", className: "border-violet-200 bg-violet-50 text-violet-800" };
  }
  if (alerts.includes("scorta_bassa")) {
    return { label: "Scorta bassa", className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  if (alerts.includes("prodotto_in_consegna")) {
    return { label: "In arrivo", className: "border-sky-200 bg-sky-50 text-sky-700" };
  }
  if (p.active === false || p.stato === "disattivo") {
    return { label: "Disattivo", className: "border-slate-200 bg-slate-50 text-slate-500" };
  }
  return { label: "Disponibile", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
}

function qtyLabel(s) {
  if (!s) return "—";
  if (s.qty_verificata != null) return `${s.qty_verificata}`;
  if (s.qty_dichiarata != null) return `${s.qty_dichiarata}`;
  if (s.unita_carepy != null) return `${s.unita_carepy}`;
  if (s.unita_residue != null) return `${s.unita_residue}`;
  return "—";
}

function KpiTile({ icon: Icon, label, value, hint, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-slate-500">{label}</p>
          <p className="mt-0.5 text-xl font-semibold tracking-tight text-slate-900">{value}</p>
          {hint ? <p className="mt-0.5 text-[10px] text-slate-400">{hint}</p> : null}
        </div>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.emerald}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function ProductDetailPanel({ product, scorta, onClose }) {
  const navigate = useNavigate();
  if (!product && !scorta) {
    return (
      <aside className="hidden w-[320px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <Package className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Prodotto selezionato</p>
          <p className="mt-1 text-xs text-slate-400">Seleziona una riga dall&apos;elenco per il dettaglio.</p>
        </div>
      </aside>
    );
  }

  const nome = product?.nome || scorta?.farmaco || "Prodotto";
  const codice = product?.codice || product?.aic || product?.minsan || "—";
  const st = productStatus(product || {}, scorta);
  const qty = qtyLabel(scorta);

  return (
    <aside className="w-full shrink-0 xl:w-[320px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0F5C4C]/10 text-[#0F5C4C]">
              <PillIcon />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold leading-snug text-slate-900">{nome}</h3>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
                  onClick={onClose}
                >
                  Chiudi
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {[product?.forma || product?.formato, product?.principio_attivo].filter(Boolean).join(" · ") ||
                  scorta?.principio_attivo ||
                  "—"}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                  {st.label}
                </Badge>
                {product ? (
                  <Badge variant="outline" className="border-emerald-200 text-[10px] text-emerald-700">
                    Catalogato
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-slate-200 text-[10px] text-slate-500">
                    Solo scorta
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4 text-xs">
          <dl className="space-y-1.5">
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Codice</dt>
              <dd className="font-mono font-medium text-slate-800">{codice}</dd>
            </div>
            {product?.ean ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">EAN</dt>
                <dd className="font-mono text-slate-800">{product.ean}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-2">
              <dt className="text-slate-500">Disponibilità (scorta)</dt>
              <dd className="font-semibold text-slate-900">{qty}</dd>
            </div>
            {scorta?.ospite_nome ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Ospite</dt>
                <dd className="truncate text-right text-slate-800">{scorta.ospite_nome}</dd>
              </div>
            ) : null}
            {scorta?.struttura_nome ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Struttura</dt>
                <dd className="truncate text-right text-slate-800">{scorta.struttura_nome}</dd>
              </div>
            ) : null}
            {product?.prezzo != null ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Prezzo listino</dt>
                <dd className="font-medium text-slate-800">{formatEuro(product.prezzo)}</dd>
              </div>
            ) : null}
            {scorta?.updated_at ? (
              <div className="flex justify-between gap-2">
                <dt className="text-slate-500">Ultimo aggiornamento</dt>
                <dd className="text-slate-800">{formatWhen(scorta.updated_at)}</dd>
              </div>
            ) : null}
          </dl>

          {(scorta?.alerts || []).length > 0 ? (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Alert</p>
              <div className="flex flex-wrap gap-1">
                {scorta.alerts.map((a) => (
                  <Badge key={a} variant="outline" className="text-[10px] border-amber-200 text-amber-800">
                    {ALERT_SCORTA_LABELS[a]?.label || a}
                  </Badge>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Azioni rapide
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate("/farmacia/scorte")}
              >
                Apri scorte / rettifica
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate("/farmacia/catalogo")}
              >
                Apri catalogo / import
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 justify-start rounded-lg text-xs"
                onClick={() => navigate("/farmacia#ordini")}
              >
                Ordini struttura
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to={product ? "/farmacia/catalogo" : "/farmacia/scorte"}>
              Apri scheda completa
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

function PillIcon() {
  return <Package className="h-5 w-5" />;
}

export default function FarmaciaMagazzino() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get("tab"))
    ? searchParams.get("tab")
    : "catalogo";

  const [prodotti, setProdotti] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [scorte, setScorte] = useState([]);
  const [soglia, setSoglia] = useState(7);
  const [rilevazioni, setRilevazioni] = useState([]);
  const [kpiExtra, setKpiExtra] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statoFilter, setStatoFilter] = useState("all");
  const [selectedKey, setSelectedKey] = useState(null);

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [cat, sco, kpi] = await Promise.all([
        farmGet("/farmacia/prodotti", token, { page: 1, limit: 100 }).catch(() => ({
          items: [],
          total: 0,
        })),
        farmGet("/farmacia/scorte", token).catch(() => ({ items: [], soglia_bassa: 7 })),
        farmGet("/farmacia/ricette/kpi", token).catch(() => ({})),
      ]);
      const products = cat.items || [];
      const stock = sco.items || [];
      setProdotti(products);
      setCatalogTotal(cat.total || products.length);
      setScorte(stock);
      setSoglia(sco.soglia_bassa ?? 7);
      setKpiExtra(kpi || {});

      // Rilevazioni recenti: campione sulle prime scorte
      const sample = stock.slice(0, 12);
      const rels = await Promise.all(
        sample.map(async (s) => {
          try {
            const res = await farmGet(
              `/farmacia/scorte/${encodeURIComponent(s.id)}/rilevazioni`,
              token
            );
            return (res.items || []).map((r) => ({
              ...r,
              scorta_id: s.id,
              farmaco: s.farmaco,
              ospite_nome: s.ospite_nome,
              struttura_nome: s.struttura_nome,
            }));
          } catch {
            return [];
          }
        })
      );
      const flat = rels
        .flat()
        .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
        .slice(0, 40);
      setRilevazioni(flat);

      setSelectedKey((prev) => {
        if (prev) return prev;
        if (products[0]) return `p:${products[0].id}`;
        if (stock[0]) return `s:${stock[0].id}`;
        return null;
      });
    } catch {
      toast.error("Impossibile caricare il magazzino");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const scortaByFarmaco = useMemo(() => {
    const map = {};
    for (const s of scorte) {
      const key = (s.farmaco || "").trim().toLowerCase();
      if (!key) continue;
      if (!map[key] || (s.alerts || []).length > (map[key].alerts || []).length) {
        map[key] = s;
      }
    }
    return map;
  }, [scorte]);

  const catalogRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return prodotti
      .map((p) => {
        const match = scortaByFarmaco[(p.nome || "").trim().toLowerCase()];
        return { key: `p:${p.id}`, product: p, scorta: match || null };
      })
      .filter((row) => {
        if (term) {
          const hay = `${row.product.nome} ${row.product.codice} ${row.product.ean} ${row.product.principio_attivo}`.toLowerCase();
          if (!hay.includes(term)) return false;
        }
        if (statoFilter === "all") return true;
        const st = productStatus(row.product, row.scorta);
        if (statoFilter === "bassa") return st.label === "Scorta bassa";
        if (statoFilter === "identificare") return st.label === "Da identificare";
        if (statoFilter === "arrivo") return st.label === "In arrivo";
        if (statoFilter === "ok") return st.label === "Disponibile";
        return true;
      });
  }, [prodotti, scortaByFarmaco, q, statoFilter]);

  const scorteRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return scorte
      .filter((s) => {
        if (!term) return true;
        const hay = `${s.farmaco} ${s.ospite_nome} ${s.struttura_nome} ${s.codice_fiscale}`.toLowerCase();
        return hay.includes(term);
      })
      .map((s) => ({ key: `s:${s.id}`, product: null, scorta: s }));
  }, [scorte, q]);

  const alertRows = useMemo(() => {
    return scorte
      .filter((s) => (s.alerts || []).length > 0)
      .filter((s) => {
        const term = q.trim().toLowerCase();
        if (!term) return true;
        return `${s.farmaco} ${s.ospite_nome}`.toLowerCase().includes(term);
      })
      .map((s) => ({ key: `s:${s.id}`, product: null, scorta: s }));
  }, [scorte, q]);

  const movimentiFiltered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rilevazioni;
    return rilevazioni.filter((r) =>
      `${r.farmaco} ${r.ospite_nome} ${r.campo} ${r.fonte}`.toLowerCase().includes(term)
    );
  }, [rilevazioni, q]);

  const tableRows =
    tab === "catalogo" ? catalogRows : tab === "scorte" ? scorteRows : tab === "alert" ? alertRows : [];

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    if (selectedKey.startsWith("p:")) {
      const p = prodotti.find((x) => `p:${x.id}` === selectedKey);
      if (!p) return null;
      return {
        product: p,
        scorta: scortaByFarmaco[(p.nome || "").trim().toLowerCase()] || null,
      };
    }
    const s = scorte.find((x) => `s:${x.id}` === selectedKey);
    return s ? { product: null, scorta: s } : null;
  }, [selectedKey, prodotti, scorte, scortaByFarmaco]);

  const kpiValues = useMemo(() => {
    const basse = scorte.filter((s) => (s.alerts || []).includes("scorta_bassa")).length;
    const daId = scorte.filter((s) => (s.alerts || []).includes("da_verificare")).length;
    const inCons = scorte.filter((s) => (s.alerts || []).includes("prodotto_in_consegna")).length;
    const alertTot = scorte.filter((s) => (s.alerts || []).length > 0).length;
    const today = new Date().toISOString().slice(0, 10);
    const movOggi = rilevazioni.filter((r) => String(r.created_at || "").startsWith(today)).length;
    const listino = prodotti.reduce((acc, p) => acc + (p.prezzo != null ? Number(p.prezzo) : 0), 0);
    return {
      prodotti: catalogTotal,
      basse: typeof kpiExtra.scorte_basse === "number" ? kpiExtra.scorte_basse : basse,
      daId,
      movOggi,
      alertTot,
      inCons,
      listino,
      conPrezzo: prodotti.filter((p) => p.prezzo != null).length,
    };
  }, [catalogTotal, scorte, rilevazioni, prodotti, kpiExtra]);

  const exportCsv = () => {
    const rows =
      tab === "catalogo"
        ? catalogRows.map((r) => [
            r.product.nome,
            r.product.codice,
            r.product.ean || "",
            r.product.prezzo ?? "",
            productStatus(r.product, r.scorta).label,
          ])
        : (tab === "scorte" || tab === "alert" ? tableRows : []).map((r) => [
            r.scorta.farmaco,
            r.scorta.ospite_nome,
            r.scorta.struttura_nome,
            qtyLabel(r.scorta),
            (r.scorta.alerts || []).join("|"),
          ]);
    const header =
      tab === "catalogo"
        ? ["Nome", "Codice", "EAN", "Prezzo", "Stato"]
        : ["Farmaco", "Ospite", "Struttura", "Qty", "Alert"];
    const csv = [header, ...rows]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `magazzino-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Esportazione avviata");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5">
          <KpiTile icon={Package} label="Prodotti attivi" value={kpiValues.prodotti} tone="emerald" />
          <KpiTile
            icon={AlertTriangle}
            label="Scorte basse"
            value={kpiValues.basse}
            hint={`Soglia ≤ ${soglia}`}
            tone="amber"
          />
          <KpiTile
            icon={PackageSearch}
            label="Da identificare"
            value={kpiValues.daId}
            hint="Carepy senza qty verificata"
            tone="violet"
          />
          <KpiTile
            icon={Truck}
            label="Rilevazioni oggi"
            value={kpiValues.movOggi}
            hint="Campione scorte caricate"
            tone="sky"
          />
          <KpiTile
            icon={AlertTriangle}
            label="Alert attivi"
            value={kpiValues.alertTot}
            hint={kpiValues.inCons ? `${kpiValues.inCons} in consegna` : undefined}
            tone="rose"
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 pt-2">
            <div className="flex flex-wrap gap-1">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`rounded-t-lg px-3 py-2 text-xs font-medium transition ${
                    tab === t.id
                      ? "border-b-2 border-[#0F5C4C] text-[#0F5C4C]"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2.5">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={
                  tab === "catalogo"
                    ? "Cerca nel catalogo…"
                    : tab === "movimenti"
                      ? "Cerca nelle rilevazioni…"
                      : "Cerca nelle scorte…"
                }
                className="h-9 rounded-xl pl-8 text-sm"
              />
            </div>
            {tab === "catalogo" ? (
              <select
                className="h-9 rounded-xl border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
                value={statoFilter}
                onChange={(e) => setStatoFilter(e.target.value)}
              >
                <option value="all">Tutti gli stati</option>
                <option value="ok">Disponibile</option>
                <option value="bassa">Scorta bassa</option>
                <option value="identificare">Da identificare</option>
                <option value="arrivo">In arrivo</option>
              </select>
            ) : null}
          </div>

          {tab === "movimenti" ? (
            <div>
              {movimentiFiltered.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-slate-400">
                  Nessuna rilevazione recente (storico completo nella pagina Scorte).
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {movimentiFiltered.map((r) => (
                    <li key={r.id || `${r.scorta_id}-${r.created_at}-${r.campo}`} className="px-4 py-3 text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800">
                            {r.fonte === "carepy"
                              ? "Carepy"
                              : r.fonte?.includes("dichiarata")
                                ? "Dichiarata"
                                : r.fonte?.includes("verificata")
                                  ? "Verificata"
                                  : r.campo}
                            {" · "}
                            <span className="font-medium text-slate-600">{r.farmaco}</span>
                          </p>
                          <p className="text-slate-400">
                            {r.ospite_nome || "—"}
                            {r.struttura_nome ? ` · ${r.struttura_nome}` : ""}
                            {r.valore != null ? ` · qty ${r.valore}` : ""}
                            {r.autore_nome ? ` · ${r.autore_nome}` : ""}
                          </p>
                        </div>
                        <span className="shrink-0 text-slate-400">{formatWhen(r.created_at)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : tableRows.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Warehouse className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessun elemento</p>
              <p className="mt-1 text-xs text-slate-400">
                {tab === "catalogo"
                  ? "Importa il catalogo prodotti o amplia la ricerca."
                  : "Le scorte si popolano dai Carepy e dalle rettifiche."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-xs">
                <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-semibold">Prodotto</th>
                    {tab === "catalogo" ? (
                      <>
                        <th className="px-3 py-2.5 font-semibold">Codice</th>
                        <th className="px-3 py-2.5 font-semibold">Disponibilità</th>
                        <th className="px-3 py-2.5 font-semibold">Prezzo</th>
                        <th className="px-3 py-2.5 font-semibold">Stato</th>
                      </>
                    ) : (
                      <>
                        <th className="px-3 py-2.5 font-semibold">Ospite</th>
                        <th className="px-3 py-2.5 font-semibold">Struttura</th>
                        <th className="px-3 py-2.5 font-semibold">Qty</th>
                        <th className="px-3 py-2.5 font-semibold">Alert</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tableRows.map((row) => {
                    const active = selectedKey === row.key;
                    if (tab === "catalogo") {
                      const st = productStatus(row.product, row.scorta);
                      return (
                        <tr
                          key={row.key}
                          className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                          onClick={() => setSelectedKey(row.key)}
                        >
                          <td className="px-4 py-3">
                            <p className="font-semibold text-slate-900">{row.product.nome}</p>
                            <p className="text-[10px] text-slate-400">
                              {[row.product.forma || row.product.formato, row.product.principio_attivo]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </p>
                          </td>
                          <td className="px-3 py-3 font-mono text-slate-600">
                            {row.product.codice || row.product.aic || "—"}
                          </td>
                          <td className="px-3 py-3 tabular-nums text-slate-700">{qtyLabel(row.scorta)}</td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.product.prezzo != null ? formatEuro(row.product.prezzo) : "—"}
                          </td>
                          <td className="px-3 py-3">
                            <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                              {st.label}
                            </Badge>
                          </td>
                        </tr>
                      );
                    }
                    const s = row.scorta;
                    return (
                      <tr
                        key={row.key}
                        className={`cursor-pointer ${active ? "bg-emerald-50/50" : "hover:bg-slate-50/80"}`}
                        onClick={() => setSelectedKey(row.key)}
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{s.farmaco}</p>
                          <p className="text-[10px] text-slate-400">{s.principio_attivo || "—"}</p>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{s.ospite_nome || "—"}</td>
                        <td className="px-3 py-3 text-slate-600">{s.struttura_nome || "—"}</td>
                        <td className="px-3 py-3 tabular-nums font-medium text-slate-800">{qtyLabel(s)}</td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(s.alerts || []).length === 0 ? (
                              <span className="text-slate-400">—</span>
                            ) : (
                              (s.alerts || []).slice(0, 2).map((a) => (
                                <Badge
                                  key={a}
                                  variant="outline"
                                  className="text-[10px] border-amber-200 text-amber-800"
                                >
                                  {ALERT_SCORTA_LABELS[a]?.label || a}
                                </Badge>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Rilevazioni recenti</h3>
            {rilevazioni.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna rilevazione nel campione</p>
            ) : (
              <ul className="space-y-2">
                {rilevazioni.slice(0, 5).map((r) => (
                  <li key={r.id || `${r.scorta_id}-${r.created_at}`} className="text-xs">
                    <p className="font-medium text-slate-800">
                      {r.fonte || r.campo} · {r.farmaco}
                    </p>
                    <p className="text-slate-400">{formatWhen(r.created_at)}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Alert magazzino</h3>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-2">
                <span className="text-slate-700">Prodotti sotto soglia</span>
                <Badge variant="outline" className="border-amber-200 text-[10px] text-amber-800">
                  {kpiValues.basse}
                </Badge>
              </li>
              <li className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-2">
                <span className="text-slate-700">Da verificare</span>
                <Badge variant="outline" className="border-violet-200 text-[10px] text-violet-800">
                  {kpiValues.daId}
                </Badge>
              </li>
              <li className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-2">
                <span className="text-slate-700">In consegna</span>
                <Badge variant="outline" className="border-sky-200 text-[10px] text-sky-700">
                  {kpiValues.inCons}
                </Badge>
              </li>
            </ul>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-800">Valore listino catalogo</h3>
            <p className="text-2xl font-semibold tracking-tight text-slate-900">
              {formatEuro(kpiValues.listino)}
            </p>
            <p className="mt-1 text-[11px] text-slate-400">
              Somma prezzi listino ({kpiValues.conPrezzo} prodotti con prezzo) — non è giacenza valorizzata.
            </p>
            <div className="mt-3 flex justify-between text-xs text-slate-600">
              <span>Prodotti catalogo</span>
              <span className="font-semibold">{kpiValues.prodotti}</span>
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-600">
              <span>Righe scorta</span>
              <span className="font-semibold">{scorte.length}</span>
            </div>
          </section>
        </div>

        <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm">
          <Button
            type="button"
            className="rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]"
            onClick={() => navigate("/farmacia/catalogo")}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Nuovo prodotto / import
          </Button>
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => navigate("/farmacia/scorte")}>
            <FileUp className="mr-1.5 h-4 w-4" />
            Gestisci scorte
          </Button>
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => setTab("alert")}>
            <AlertTriangle className="mr-1.5 h-4 w-4" />
            Vedi alert
          </Button>
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => navigate("/farmacia/catalogo")}>
            <BookOpen className="mr-1.5 h-4 w-4" />
            Catalogo completo
          </Button>
          <Button type="button" variant="outline" className="ml-auto rounded-xl" onClick={exportCsv}>
            <Download className="mr-1.5 h-4 w-4" />
            Esporta inventario
          </Button>
        </div>

        {selected ? (
          <div className="xl:hidden">
            <ProductDetailPanel
              product={selected.product}
              scorta={selected.scorta}
              onClose={() => setSelectedKey(null)}
            />
          </div>
        ) : null}
      </div>

      <div className="hidden xl:block">
        <ProductDetailPanel
          product={selected?.product}
          scorta={selected?.scorta}
          onClose={() => setSelectedKey(null)}
        />
      </div>
    </div>
  );
}
